import { Article, BookmarkBorder, CalendarToday, OpenInNew, Refresh, TrendingUp } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const Weblog = () => {
    const { t } = useTranslation();
    const { mode } = useTheme();
    const { isRTL } = useLanguage();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Static AI news content
    const staticArticles = [
        {
            id: 1,
            title: "OpenAI launches AgentKit to help developers build and ship AI agents",
            description: "OpenAI has introduced AgentKit that enables developers to quickly build and deploy AI agents. New applications have also been released within ChatGPT.",
            link: "https://techcrunch.com/category/artificial-intelligence",
            pubDate: new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            author: "TechCrunch",
            points: 1247
        },
        {
            id: 2,
            title: "New AI training method creates powerful software agents with just 78 examples",
            description: "Researchers from Shanghai Jiao Tong University in collaboration with SII Generative AI Lab have developed a new method that creates powerful software agents with just 78 examples.",
            link: "https://venturebeat.com/category/ai",
            pubDate: new Date(Date.now() - 86400000).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            author: "VentureBeat",
            points: 892
        },
        {
            id: 3,
            title: "Google is expanding AI Mode to more languages",
            description: "Google has expanded AI Mode to over 35 new languages and 40 additional countries and regions to provide broader global access.",
            link: "https://theverge.com/ai-artificial-intelligence",
            pubDate: new Date(Date.now() - 172800000).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            author: "The Verge",
            points: 1563
        },
        {
            id: 4,
            title: "49 US AI startups raising $100M+ in 2024",
            description: "In 2024, 49 US AI startups have each raised over $100 million in funding; applications range from smart glasses to climate solutions.",
            link: "https://techcrunch.com/tag/artificial-intelligence",
            pubDate: new Date(Date.now() - 259200000).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            author: "TechCrunch",
            points: 2105
        }
    ];

    const fetchArticles = async () => {
        setLoading(true);
        setError(null);

        try {
            // Simulate loading delay for better UX
            await new Promise(resolve => setTimeout(resolve, 1000));
            setArticles(staticArticles);
        } catch (err) {
            console.error('Error loading articles:', err);
            setError(t('news.error'));
            setArticles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    const handleRefresh = () => {
        fetchArticles();
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(ellipse at top, #1e3a5f 0%, #0f2642 100%)'
                            : 'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)',
                    zIndex: 0,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                        'radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), ' +
                        'radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Hero Section */}
                <Box
                    sx={{
                        py: { xs: 6, sm: 8, md: 12 },
                        textAlign: isRTL ? 'right' : 'center',
                        position: 'relative',
                        zIndex: 1,
                        px: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem', lg: '4rem' },
                            fontWeight: 900,
                            mb: { xs: 2, sm: 3 },
                            background: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                                    : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {t('news.title')}
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                            fontWeight: 600,
                            mb: 4,
                            background: 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        {t('news.subtitle')}
                    </Typography>

                    {/* Feature Chips */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: isRTL ? 'flex-end' : 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 4,
                        }}
                    >
                        <Chip
                            icon={<TrendingUp />}
                            label={t('news.tabs.trendingTopics')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                        <Chip
                            icon={<Article />}
                            label={t('news.tabs.aiArticles')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                        <Chip
                            icon={<BookmarkBorder />}
                            label={t('news.tabs.latestUpdates')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        disabled={loading}
                        sx={{
                            px: 4,
                            py: 2,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                            '&:hover': {
                                boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {loading ? t('news.loading') : t('news.refreshButton')}
                    </Button>
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-end' : 'center', my: 8 }}>
                        <CircularProgress
                            sx={{
                                color: '#2196f3',
                                '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert
                        severity="warning"
                        sx={{
                            mb: 4,
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255, 193, 7, 0.1)'
                                    : 'rgba(255, 193, 7, 0.1)',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 2,
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Articles Grid */}
                {!loading && articles.length > 0 && (
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                        {articles.map((article) => (
                            <Grid item xs={12} sm={6} md={6} lg={4} key={article.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: 3,
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            border: '1px solid rgba(33, 150, 243, 0.3)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        {/* Article Header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                            <Article sx={{ color: '#2196f3', fontSize: '1.2rem' }} />
                                            <Chip
                                                label={t('news.tags.aiNews')}
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                    color: '#2196f3',
                                                    border: '1px solid rgba(33, 150, 243, 0.3)',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </Box>

                                        {/* Article Title */}
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontSize: { xs: '1.1rem', md: '1.3rem' },
                                                fontWeight: 600,
                                                mb: 2,
                                                lineHeight: 1.3,
                                                color: (theme) =>
                                                    theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textAlign: isRTL ? 'right' : 'left',
                                            }}
                                        >
                                            {article.title}
                                        </Typography>

                                        {/* Article Description */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.95rem',
                                                lineHeight: 1.6,
                                                mb: 3,
                                                color: (theme) =>
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.7)'
                                                        : 'rgba(0, 0, 0, 0.6)',
                                                flexGrow: 1,
                                                textAlign: isRTL ? 'right' : 'left',
                                            }}
                                        >
                                            {article.description}
                                        </Typography>

                                        {/* Article Footer */}
                                        <Box sx={{ mt: 'auto' }}>
                                            {/* Article Meta Info */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                                {article.author && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? 'rgba(255, 255, 255, 0.6)'
                                                                    : 'rgba(0, 0, 0, 0.5)',
                                                            fontSize: '0.8rem',
                                                            fontStyle: 'italic',
                                                            textAlign: isRTL ? 'right' : 'left',
                                                        }}
                                                    >
                                                        {t('news.tags.by')} {article.author}
                                                    </Typography>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                                    <CalendarToday sx={{ color: '#2196f3', fontSize: '1rem' }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: (theme) =>
                                                                theme.palette.mode === 'dark'
                                                                    ? 'rgba(255, 255, 255, 0.6)'
                                                                    : 'rgba(0, 0, 0, 0.5)',
                                                            fontSize: '0.8rem',
                                                            textAlign: isRTL ? 'right' : 'left',
                                                        }}
                                                    >
                                                        {article.pubDate}
                                                    </Typography>
                                                </Box>

                                                {article.points && (
                                                    <Chip
                                                        label={`${article.points} ${t('news.tags.points')}`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                            color: '#2196f3',
                                                            border: '1px solid rgba(33, 150, 243, 0.3)',
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Read More Button */}
                                            <Button
                                                variant="outlined"
                                                endIcon={<OpenInNew />}
                                                href={article.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                fullWidth
                                                sx={{
                                                    py: 1.5,
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    borderColor: '#2196f3',
                                                    color: '#2196f3',
                                                    backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                                    paddingRight: isRTL ? '16px' : undefined,
                                                    paddingLeft: isRTL ? undefined : '16px',
                                                    '&:hover': {
                                                        borderColor: '#1976d2',
                                                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                                                    },
                                                    transition: 'all 0.3s ease',
                                                }}
                                            >
                                                {t('news.tags.readMore')}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* No Articles State */}
                {!loading && !error && articles.length === 0 && (
                    <Box sx={{ textAlign: isRTL ? 'right' : 'center', my: 8 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                                mb: 2,
                                fontWeight: 600,
                            }}
                        >
                            {t('news.noArticles.title')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.7)'
                                        : 'rgba(0, 0, 0, 0.6)',
                                mb: 3,
                            }}
                        >
                            {t('news.noArticles.description')}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleRefresh}
                            sx={{
                                px: 4,
                                py: 2,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                    boxShadow: '0 12px 32px rgba(102, 126, 234, 0.6)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {t('news.noArticles.tryAgain')}
                        </Button>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default Weblog;
